import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { cn } from "@/utils/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import {
  Groups,
  ArrowDown,
  Sort,
  Cards,
  Delta,
  InfoCircle,
  Unhide,
  Pencil,
  ChevronRight
} from "@dreamhub/designsystem/icons";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import CompareToSelect from "./CompareToSelect";
import type { CompareToSelection } from "@/entities/dataTables/compareTo/types";
import SingleSelect from "@/components/controls/SingleSelect";
import Tabs from "@/components/controls/Tabs";
import { SortingState, Updater } from "@tanstack/react-table";
import { Divider } from "@/components/ui/divider";
import { ColumnData } from "@/entities/dataTables/types";
import {
  DateBucketOptionLabel,
  mapFieldsToGroupByOptions,
  mapFiltersToGroupByOptions,
  getTempView,
  updateTempViewColumnsState
} from "@/entities/dataTables/helpers";
import { parseDateBucketId } from "@/entities/dataTables/monthGrouping";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import { ViewDataConfiguration } from "@/entities/views/types";
import { APIFieldItem } from "@/pages/settings/types";
import DisplayPopoverColumnManagement from "./DisplayPopoverColumnManagement";
import { useDisplaySettingsChanged } from "../useDisplaySettingsChanged";

const TRANSLATION_KEY = "datatoolbar.display";

/**
 * Every labelled row's left column is pinned to the same width so the controls
 * beside them share one left edge. A min-width is not enough: "Compare to"
 * carries a tooltip button the other labels don't, and its content alone
 * overruns 120px, which is what pushed its select out of line with the two
 * above it.
 */
// The width is fixed rather than a min-width, so a label longer than the box (a
// new locale, or the per-account translation overrides in
// `services/translation/hook.tsx`) has to give way somewhere. The label's own
// text truncates and everything else in the row keeps its size, so the tooltip
// trigger beside it stays visible and clickable rather than being clipped out of
// reach; `overflow-hidden` is the backstop for anything that still would not fit.
// No effect on any string that already fits.
/**
 * Narrows one deserialized `config.columns` entry before it is dereferenced.
 * `isVisible` has to be a real boolean, not merely present: the stored JSON can
 * hold `"false"`, which is a truthy string and would count a hidden column as
 * shown.
 */
const isColumnData = (value: unknown): value is ColumnData =>
  typeof value === "object" &&
  value !== null &&
  "isVisible" in value &&
  typeof (value as { isVisible: unknown }).isVisible === "boolean";

const ROW_LABEL_CLASS =
  "flex items-center gap-2 w-[8.75rem] shrink-0 overflow-hidden pl-1";

export interface DisplayPopoverProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  groupBy?: string[];
  setGroupBy?: (groupBy: string[]) => void;
  /** Group-by options for columns no groupable filter reaches. */
  groupByOptions?: string[];
  /** The table's filters; each groupable one offers its column in Group By. */
  groupByFilterOptions?: DataTableFilter[];
  sorting?: SortingState;
  onSortingChange?: (updater: Updater<SortingState>) => void;
  sortByOptions?: string[];
  defaultSortField?: string;
  onReset?: () => void;
  className?: string;
  children: React.ReactNode;
  selectedView?: ViewDataConfiguration;
  columnsState?: ColumnData[];
  defaultColumnsState?: ColumnData[];
  /** Stops the first default column being hidden, and being moved while it is at the top of the column list. */
  lockFirstColumn?: boolean;
  onColumnsApply?: (updatedColumns: ColumnData[]) => void;
  tableId?: string;
  customFields?: APIFieldItem[];
  // Field metadata used to derive groupable options. Defaults to customFields;
  // tables that pass a filtered customFields (e.g. Contracts/Services strip
  // their hardcoded system columns) can pass the full field list here so system
  // and linked columns still surface in Group By.
  groupByFields?: APIFieldItem[];
  hasChanges?: boolean;
  showTableColumns?: boolean;
  showAsValue?: string;
  onShowAsChange?: (value: string) => void;
  showAsOptions?: { value: string; label: React.ReactNode }[];
  // Compare-to. Both are required for the row to render, so a table
  // that does not support the feature — or has it behind a closed flag — simply
  // leaves them off and the Display menu is unchanged.
  compareTo?: CompareToSelection;
  onCompareToChange?: (selection: CompareToSelection) => void;
  /** The selection Reset returns Compare to to. */
  defaultCompareTo?: CompareToSelection;
  /**
   * Column ids withheld from the Sort by list because the API has no field to
   * order them by. Named for the menu it filters — `unsortableColumnIds` in
   * `dataTables/helpers` is a different gate (it sets `enableSorting` on a
   * registry column) and the two are maintained independently.
   */
  sortMenuExcludedColumnIds?: ReadonlySet<string>;
  /**
   * Offers each date column by month, quarter and year. Only for a table that builds
   * the quarter and year grouping columns itself (`buildDateBucketColumn`).
   */
  groupByDateBuckets?: boolean;
}

const DisplayPopover: React.FC<DisplayPopoverProps> = ({
  isOpen,
  onOpenChange,
  groupBy = [],
  setGroupBy,
  groupByOptions = [],
  groupByFilterOptions,
  sorting = [],
  onSortingChange,
  sortByOptions = [],
  defaultSortField = "name",
  onReset,
  className,
  children,
  selectedView,
  columnsState = [],
  defaultColumnsState = [],
  lockFirstColumn = true,
  onColumnsApply,
  tableId = "",
  customFields,
  groupByFields,
  showTableColumns = true,
  showAsValue,
  onShowAsChange,
  showAsOptions,
  compareTo,
  onCompareToChange,
  defaultCompareTo,
  sortMenuExcludedColumnIds,
  groupByDateBuckets = false
}) => {
  const { t } = useTranslation();

  // All three are required together: without `defaultCompareTo`, Reset has
  // nothing to return the selection to (it silently no-ops) while the changed
  // indicator would still light up for a selection it can never clear.
  const compareToEnabled =
    !!compareTo && !!onCompareToChange && !!defaultCompareTo;

  const showAsTabsOptions = useMemo(() => {
    const optionsMap: { [key: string]: string | JSX.Element } = {};
    (showAsOptions ?? []).forEach((option) => {
      optionsMap[option.value] = option.label as string | JSX.Element;
    });
    return optionsMap;
  }, [showAsOptions]);

  const showAsEnabled = !!onShowAsChange && (showAsOptions?.length ?? 0) > 0;

  const [showColumnsSection, setShowColumnsSection] = useState(false);

  const { currentSortField, currentSortDesc, hasColumnsChanged, hasChanges } =
    useDisplaySettingsChanged({
      groupBy,
      sorting,
      defaultSortField,
      compareTo,
      defaultCompareTo,
      columnsState,
      defaultColumnsState
    });

  const handleReset = () => {
    if (setGroupBy) {
      setGroupBy([]);
    }
    if (onSortingChange) {
      onSortingChange([{ id: defaultSortField, desc: false }]);
    }
    if (onCompareToChange && defaultCompareTo) {
      onCompareToChange(defaultCompareTo);
    }
    // The temp view is written too, as Customize does, so a reload or view
    // re-activation doesn't bring the old columns back.
    if (onColumnsApply && hasColumnsChanged) {
      if (selectedView) {
        updateTempViewColumnsState(defaultColumnsState, selectedView);
      }
      onColumnsApply(defaultColumnsState);
    }
    if (onReset) {
      onReset();
    }
    toast.success(t(`${TRANSLATION_KEY}.resetSuccess`), {
      duration: 5000
    });
  };

  useEffect(() => {
    if (!isOpen) {
      setShowColumnsSection(false);
    }
  }, [isOpen]);

  // `columnsState` is empty until the table seeds it from the defaults, which is
  // gated on async reads on some pages. Everything that has to answer "which
  // column set is authoritative right now" reads this one value, so the answer
  // cannot drift between the three of them.
  const effectiveColumnsState = useMemo(
    () => (columnsState.length > 0 ? columnsState : defaultColumnsState),
    [columnsState, defaultColumnsState]
  );

  // Grouping and sorting are applied client-side by TanStack, which can only
  // act on a column that is actually present/visible in the table. Reveal the
  // target column (or append it from the defaults) so grouping/sorting by a
  // hidden custom/linked field still works.
  const ensureColumnVisible = (columnId: string) => {
    if (!onColumnsApply) return;

    // Operate on whichever set currently backs the options — otherwise
    // selecting a fallback option would silently leave its column hidden.
    const baseColumnsState = effectiveColumnsState;
    if (baseColumnsState.length === 0) return;

    const column = baseColumnsState.find((col) => col.id === columnId);
    if (column) {
      if (column.isVisible === false) {
        onColumnsApply(
          baseColumnsState.map((col) =>
            col.id === columnId ? { ...col, isVisible: true } : col
          )
        );
      }
    } else {
      const defaultColumn = defaultColumnsState.find(
        (col) => col.id === columnId
      );
      if (defaultColumn) {
        onColumnsApply([
          ...baseColumnsState,
          { ...defaultColumn, isVisible: true }
        ]);
      }
    }
  };

  const handleSortFieldChange = (field: string) => {
    if (onSortingChange) {
      onSortingChange([{ id: field, desc: currentSortDesc }]);
    }

    ensureColumnVisible(field);
  };

  const handleSortDirectionToggle = () => {
    if (onSortingChange && currentSortField) {
      onSortingChange([{ id: currentSortField, desc: !currentSortDesc }]);
    }
  };

  const handleGroupByChange = (value: string) => {
    if (!setGroupBy) return;

    if (value === "none") {
      setGroupBy([]);
      return;
    }

    setGroupBy([value]);
    // A quarter or year groups on a column the table builds from the date column, so
    // it is the date column that has to be shown.
    ensureColumnVisible(parseDateBucketId(value)?.baseId ?? value);
  };

  const dateBucketLabel = useMemo<DateBucketOptionLabel | undefined>(
    () =>
      groupByDateBuckets
        ? (label, bucket) =>
            t(`datatoolbar.filters.dateBucket.${bucket}`, { label })
        : undefined,
    [groupByDateBuckets, t]
  );

  const fieldGroupByOptions = useMemo(
    () =>
      mapFieldsToGroupByOptions(
        effectiveColumnsState,
        groupByFields ?? customFields,
        defaultColumnsState,
        dateBucketLabel
      ),
    [
      effectiveColumnsState,
      defaultColumnsState,
      groupByFields,
      customFields,
      dateBucketLabel
    ]
  );

  const filterGroupByOptions = useMemo(
    () =>
      mapFiltersToGroupByOptions(
        groupByFilterOptions,
        effectiveColumnsState,
        defaultColumnsState,
        dateBucketLabel
      ),
    [
      groupByFilterOptions,
      effectiveColumnsState,
      defaultColumnsState,
      dateBucketLabel
    ]
  );

  const groupBySelectOptions = useMemo(() => {
    // Same column set the derived options resolve against: a listed column the table
    // doesn't show (hidden by Field Management) has nothing to group.
    // Live columns first, so their label wins over the default's.
    const columnLabels = new Map<string, string>();
    [...effectiveColumnsState, ...defaultColumnsState].forEach((column) => {
      if (!columnLabels.has(column.id))
        columnLabels.set(column.id, column.label);
    });
    const optionsById = new Map<string, { id: string; displayName: string }>();
    groupByOptions
      .filter((option) =>
        columnLabels.has(parseDateBucketId(option)?.baseId ?? option)
      )
      .forEach((option) =>
        optionsById.set(option, {
          id: option,
          // An option with no label of its own, such as a column whose header
          // names its currency, is labelled as its column is.
          displayName: t(
            `datatoolbar.filters.sortAndGroupByOptions.${option}`,
            {
              defaultValue: columnLabels.get(option) || option
            }
          )
        })
      );
    [...filterGroupByOptions, ...fieldGroupByOptions].forEach((option) => {
      if (!optionsById.has(option.id)) optionsById.set(option.id, option);
    });
    return [
      { id: "none", displayName: t(`${TRANSLATION_KEY}.noGrouping`) },
      ...optionsById.values()
    ];
  }, [
    groupByOptions,
    effectiveColumnsState,
    defaultColumnsState,
    filterGroupByOptions,
    fieldGroupByOptions,
    t
  ]);

  const showGroupBySearch = groupBySelectOptions.length - 1 > 10;

  // Null means "cannot be known from here", and the caption then says nothing
  // rather than guessing. `DataTable` resolves visibility from columnsState, then
  // the temp view, then an `initialColumnVisibility` this component never
  // receives — DealsTable passes one that hides columns, Contracts passes none at
  // all — so once both of the first two are empty there is no answer that is right
  // for every table. Reporting all columns overstates Deals; reporting only the
  // visible defaults under-reports Contracts. `effectiveColumnsState` is
  // deliberately not reused: it answers which set backs the group-by/sort options,
  // a different question with a different fallback.
  const visibleColumnCount = useMemo<number | null>(() => {
    if (columnsState.length > 0) {
      return columnsState.filter((column) => column.isVisible).length;
    }
    // `getTempView` parses sessionStorage and normalizes only filters, so
    // `config.columns` is whatever was stored: a truthy non-array with a
    // `length` (a string, say) would reach `.filter`, and a persisted `[null]`
    // would be dereferenced, either way taking the render down. A stored view
    // that cannot be read is an unknown count, not a wrong one, so a malformed
    // entry falls through rather than being counted as hidden.
    const tempViewColumns: unknown = getTempView(selectedView)?.config?.columns;
    if (Array.isArray(tempViewColumns) && tempViewColumns.length > 0) {
      return tempViewColumns.every(isColumnData)
        ? tempViewColumns.filter((column) => column.isVisible).length
        : null;
    }
    return null;
  }, [columnsState, selectedView]);

  const sortBySelectOptions = useMemo(() => {
    if (defaultColumnsState.length > 0) {
      return defaultColumnsState
        .filter((column) => !sortMenuExcludedColumnIds?.has(column.id))
        .map((column) => ({
          id: column.id,
          displayName:
            column.label ||
            t(`datatoolbar.filters.sortAndGroupByOptions.${column.id}`)
        }));
    }
    return sortByOptions
      .filter((option) => !sortMenuExcludedColumnIds?.has(option))
      .map((option) => ({
        id: option,
        displayName: t(`datatoolbar.filters.sortAndGroupByOptions.${option}`)
      }));
  }, [defaultColumnsState, sortByOptions, sortMenuExcludedColumnIds, t]);

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={4}
        className={cn("w-[22.5rem] flex flex-col gap-2", className)}
      >
        <div className="flex flex-col gap-4">
          {setGroupBy && groupBySelectOptions.length > 1 && (
            <div className="flex items-center">
              <div className={ROW_LABEL_CLASS}>
                <IconContainer color="secondary" size="5" className="shrink-0">
                  <Groups />
                </IconContainer>
                <Text variant="bodySecondary" className="truncate min-w-0">
                  {t(`${TRANSLATION_KEY}.groupBy`)}
                </Text>
              </div>
              <div className="flex-1 min-w-0">
                <SingleSelect
                  fieldName="groupBy"
                  options={groupBySelectOptions}
                  selectedValue={groupBy[0] || "none"}
                  onSelection={(value) => handleGroupByChange(value as string)}
                  variant="form"
                  showSearch={showGroupBySearch}
                  popoverClassName="w-[var(--radix-popover-trigger-width)] min-w-0"
                />
              </div>
            </div>
          )}
          {onSortingChange && sortBySelectOptions.length > 0 && (
            <div className="flex items-center">
              <div className={ROW_LABEL_CLASS}>
                <IconContainer color="secondary" size="5" className="shrink-0">
                  <Sort />
                </IconContainer>
                <Text variant="bodySecondary" className="truncate min-w-0">
                  {t(`${TRANSLATION_KEY}.sortBy`)}
                </Text>
              </div>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="flex-1 min-w-0">
                  <SingleSelect
                    fieldName="sortBy"
                    options={sortBySelectOptions}
                    selectedValue={currentSortField}
                    onSelection={(value) =>
                      handleSortFieldChange(value as string)
                    }
                    variant="form"
                    popoverClassName="w-[calc(var(--radix-popover-trigger-width)+2.75rem)] min-w-0"
                  />
                </div>
                <IconButton
                  variant="icon"
                  size="xl"
                  onClick={handleSortDirectionToggle}
                  disabled={!currentSortField}
                  iconClassName={cn(
                    "transition-transform duration-200",
                    !currentSortDesc && "rotate-180"
                  )}
                >
                  <ArrowDown />
                </IconButton>
              </div>
            </div>
          )}
          {showAsEnabled && (
            <div className="flex items-center">
              <div className={ROW_LABEL_CLASS}>
                <IconContainer color="secondary" size="5" className="shrink-0">
                  <Cards />
                </IconContainer>
                <Text variant="bodySecondary" className="truncate min-w-0">
                  {t(`${TRANSLATION_KEY}.showAs`)}
                </Text>
              </div>
              <div className="flex items-center justify-end flex-1 min-w-0">
                <Tabs
                  options={showAsTabsOptions}
                  value={showAsValue}
                  valueSelected={(value) => onShowAsChange?.(value)}
                />
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end">
          <Button
            variant="tertiary"
            onClick={handleReset}
            disabled={!hasChanges}
          >
            {t(`${TRANSLATION_KEY}.reset`)}
          </Button>
        </div>
        {showTableColumns && (
          <>
            <Divider className="my-1" dividerYPadding="0" />
            {!showColumnsSection && (
              <Text variant="smBodyTertiary" className="pl-1">
                {t(`${TRANSLATION_KEY}.tableColumns`)}
              </Text>
            )}
            {compareToEnabled && !showColumnsSection && (
              <div className="flex items-center">
                <div className={ROW_LABEL_CLASS}>
                  <IconContainer color="secondary" size="5" className="shrink-0">
                    <Delta />
                  </IconContainer>
                  <Text variant="bodySecondary" className="truncate min-w-0">
                    {t(`${TRANSLATION_KEY}.compareTo.label`)}
                  </Text>
                  <Tooltip>
                    {/* A real button rather than a span: the tooltip's
                        content is otherwise unreachable by keyboard. */}
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label={t(`${TRANSLATION_KEY}.compareTo.tooltip`)}
                        className="flex shrink-0"
                      >
                        <IconContainer color="secondary">
                          <InfoCircle />
                        </IconContainer>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[16rem]">
                      <Text variant="body">
                        {t(`${TRANSLATION_KEY}.compareTo.tooltip`)}
                      </Text>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="flex-1 min-w-0">
                  <CompareToSelect value={compareTo} onChange={onCompareToChange} />
                </div>
              </div>
            )}
            {!showColumnsSection ? (
              <div className="flex items-center justify-between gap-3">
                {visibleColumnCount !== null && (
                  <div className="flex items-center gap-2 pl-1 min-w-0">
                    <IconContainer
                      color="secondary"
                      size="5"
                      className="shrink-0"
                    >
                      <Unhide />
                    </IconContainer>
                    <Text variant="bodySecondary" className="truncate">
                      {t(`${TRANSLATION_KEY}.columnsShown`, {
                        count: visibleColumnCount
                      })}
                    </Text>
                  </div>
                )}
                {/* `ml-auto` keeps the button right-aligned in the window where
                    the count is withheld and it is the row's only child. */}
                <Button
                  variant="tertiary"
                  className="gap-1 shrink-0 ml-auto"
                  onClick={() => setShowColumnsSection(true)}
                >
                  {/* `span` throughout: a native button admits phrasing
                      content only, so IconContainer's default div and Text's
                      default p are both invalid here. The glyphs are decorative
                      — the visible label names the button — so they are hidden
                      from assistive tech rather than announced. */}
                  <IconContainer
                    as="span"
                    aria-hidden="true"
                    size="5"
                    color="cta"
                    className="[&>svg]:size-4"
                  >
                    <Pencil />
                  </IconContainer>
                  <Text as="span" variant="body" className="text-cta">
                    {t(`${TRANSLATION_KEY}.customize`)}
                  </Text>
                  <IconContainer
                    as="span"
                    aria-hidden="true"
                    size="5"
                    color="cta"
                    className="[&>svg]:size-4"
                  >
                    <ChevronRight />
                  </IconContainer>
                </Button>
              </div>
            ) : (
              <DisplayPopoverColumnManagement
                isOpen={isOpen && showColumnsSection}
                selectedView={selectedView}
                defaultColumnsState={defaultColumnsState}
                lockFirstColumn={lockFirstColumn}
                columnsState={columnsState}
                onColumnsApply={onColumnsApply}
                tableId={tableId}
                customFields={customFields}
                onClose={() => setShowColumnsSection(false)}
              />
            )}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default DisplayPopover;
